import type { PixelCrop } from 'react-image-crop'

/**
 * Como o arquivo final sai do corte. O objetivo é poupar cota e banda: a foto de
 * celular de 4000 px vira um WebP no tamanho que o destino realmente usa.
 */
export interface CropOutput {
    /** Largura máxima do arquivo final em px; o corte maior que isso é reduzido. */
    maxWidth?: number
    /** Tipo do arquivo final (padrão WebP, que preserva transparência). */
    mimeType?: 'image/webp' | 'image/jpeg' | 'image/png'
    /** Qualidade da compressão com perdas (padrão 0.85). */
    quality?: number
}

const DEFAULT_MIME = 'image/webp'
const DEFAULT_QUALITY = 0.85

function toBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob | null> {
    return new Promise((resolve) => canvas.toBlob(resolve, type, quality))
}

function renameForMime(name: string, mimeType: string): string {
    const base = name.replace(/\.[^.]+$/, '') || 'imagem'
    const extension = mimeType === 'image/webp' ? 'webp' : mimeType === 'image/png' ? 'png' : 'jpg'
    return `${base}.${extension}`
}

/**
 * Recorta a área escolhida da imagem exibida e devolve o arquivo final.
 *
 * O `crop` do react-image-crop vem em px do elemento EXIBIDO; aqui ele é levado
 * ao tamanho natural da imagem (`naturalWidth / width`) e desenhado num canvas
 * do tamanho de saída — nunca o canvas de preview, e sem `devicePixelRatio`,
 * para o arquivo não variar com o monitor de quem edita.
 */
export async function cropImageToFile(
    image: HTMLImageElement,
    crop: PixelCrop,
    originalName: string,
    output: CropOutput = {},
): Promise<File> {
    const { maxWidth, mimeType = DEFAULT_MIME, quality = DEFAULT_QUALITY } = output

    const scaleX = image.naturalWidth / image.width
    const scaleY = image.naturalHeight / image.height

    const sourceX = crop.x * scaleX
    const sourceY = crop.y * scaleY
    const sourceWidth = crop.width * scaleX
    const sourceHeight = crop.height * scaleY

    // Reduz para a largura alvo; nunca amplia (ampliar só borra e gasta cota).
    const targetWidth = Math.round(maxWidth ? Math.min(sourceWidth, maxWidth) : sourceWidth)
    const targetHeight = Math.round((targetWidth / sourceWidth) * sourceHeight)

    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, targetWidth)
    canvas.height = Math.max(1, targetHeight)

    const context = canvas.getContext('2d')
    if (!context) throw new Error('Não foi possível preparar o corte da imagem')
    context.imageSmoothingQuality = 'high'
    context.drawImage(
        image,
        sourceX,
        sourceY,
        sourceWidth,
        sourceHeight,
        0,
        0,
        canvas.width,
        canvas.height,
    )

    // Navegador sem encoder WebP (raro hoje) cai para JPEG em vez de falhar.
    let finalMime = mimeType
    let blob = await toBlob(canvas, finalMime, quality)
    if (!blob && finalMime === 'image/webp') {
        finalMime = 'image/jpeg'
        blob = await toBlob(canvas, finalMime, quality)
    }
    if (!blob) throw new Error('Não foi possível gerar a imagem cortada')

    return new File([blob], renameForMime(originalName, finalMime), { type: finalMime })
}

/** Lê o arquivo como data URL para o `<img>` do diálogo de corte. */
export function fileToDataUrl(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(String(reader.result ?? ''))
        reader.onerror = () => reject(reader.error ?? new Error('Não foi possível ler o arquivo'))
        reader.readAsDataURL(file)
    })
}

/**
 * O corte não se aplica a tudo que chega num campo de imagem: GIF perderia a
 * animação no canvas, e SVG não tem pixel para recortar. Esses sobem como vieram.
 */
export function isCroppableImage(file: File): boolean {
    if (!file.type.startsWith('image/')) return false
    return file.type !== 'image/gif' && file.type !== 'image/svg+xml'
}
